import React, { useState, useEffect } from 'react';
import {
  Button, Col, DatePicker, Input, Modal, Row,
  notification, Select, Spin, Table, Form,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SquareEditOutlineIcon from 'mdi-react/SquareEditOutlineIcon';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import FilterIcon from 'mdi-react/FilterIcon';
import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import moment, { Moment } from 'moment';
import { namespaces } from '../../../i18n/i18n.constants';
import { CoopercitrusUser, ResponsibleUserParams } from '../../../sharedTypes/UserTypes';
import {
  ServiceStatus, Service, ServiceRequestForCustomer,
  ServiceRequestForCoopercitrus, FlowOutput,
} from '../../../sharedTypes/ServiceTypes';
import ServiceStatusComponent from '../../sharedComponents/serviceStatusComponent/ServiceStatusComponent';
import {
  put, patch, get, post, isSuperAgentError,
  ResponseWrapper, RequestWrapper,
} from '../../../dataSource/requests';
import { backendResponseCodes } from '../../../utils/responseMessages';
import { getUserType } from '../../../utils/users';
import { formatBP } from '../../../utils/format';
import CustomPagination from '../../sharedComponents/customPagination/CustomPagination';
import './ListServices.scss';

const ListServices = (): JSX.Element => {
  interface RelevantPaginationInfo {
    maxSize: number;
    numberOfPages: number;
    pageNumber: number;
    totalElements: number;
  }

  const { t } = useTranslation('namespaces');
  const navigate = useNavigate();
  const [scheduleFormInstance] = Form.useForm();
  const [searchFormInstance] = Form.useForm();
  const [userFormInstance] = Form.useForm();

  const [userType] = useState<string | null>(getUserType());
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);
  const [servicesFilter, setServicesFilter] = useState<Array<JSX.Element>>([]);
  const [allActivatedUsers, setAllActivatedUsers] = useState<Array<CoopercitrusUser>>([]);
  const [userOptions, setUserOptions] = useState<Array<JSX.Element>>([]);
  const [statusArray, setStatusArray] = useState<Array<ServiceStatus>>([]);
  const [
    data, setData,
  ] = useState<Array<ServiceRequestForCustomer | ServiceRequestForCoopercitrus>>([]);
  const [maxPeriodOfDaysFilter, setMaxPeriodOfDaysFilter] = useState<number>(30);
  const [paginationInfo, setPaginationInfo] = useState<RelevantPaginationInfo>({
    maxSize: 15,
    numberOfPages: 1,
    pageNumber: 1,
    totalElements: 1,
  });
  const [modalScheduleDate, setModalScheduleDate] = useState<boolean>(false);
  const [visibleModalResponsable, setVisibleModalResponsable] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [userModalOptions, setUserModalOptions] = useState<Array<JSX.Element>>([]);
  const [currentModalServiceId, setCurrentModalServiceId] = useState<number>(0);
  const [visibleModalStatus, setVisibleModalStatus] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [statusesList, setStatusesList] = useState<Array<FlowOutput>>([]);
  const [currentStatusIndex, setCurrentStatusIndex] = useState<number>(0);
  const [canceled, setCanceled] = useState<boolean>(false);
  const [paramsResponsableUser, setParamsResponsableUser] = useState<ResponsibleUserParams>({});

  const clearFormModals = (): void => {
    scheduleFormInstance.resetFields();
    userFormInstance.resetFields();
  };

  const filterOptions = (input: any, option: any): boolean => option?.['props']
    .children
    .toLowerCase()
    .includes(input.toLowerCase());

  const validateScheduleDate = (_rule: any, value: Moment): Promise<void | Error> => {
    if (!value) {
      return Promise.reject(new Error('Por favor, insira data de agendamento.'));
    }

    const dateFormat = 'DD/MM/YYYY';

    if (moment(value).endOf('day') < moment().endOf('day')) {
      return Promise.reject(new Error(`Insira uma data maior ou igual a ${moment().format(dateFormat)}`));
    }

    return Promise.resolve();
  };

  const getFilterParameters = (): object => {
    const values = searchFormInstance.getFieldsValue();

    const params: {
      serviceName?: string;
      ctc?: string;
      bp?: string;
      landOwnerName?: string;
      landName?: string;
      responsableUserId?: number;
      serviceId?: number;
      statusId?: number;
      periodStart?: string;
      periodEnd?: string;
    } = {};

    if (values.serviceFilter) {
      params.serviceName = values.serviceFilter;
    }

    if (values.ctcFilter) {
      params.ctc = values.ctcFilter;
    }

    if (values.matricularionFilter) {
      params.bp = values.matricularionFilter;
    }

    if (values.cooperativeFilter) {
      params.landOwnerName = values.cooperativeFilter;
    }

    if (values.propertyFilter) {
      params.landName = values.propertyFilter;
    }

    if (values.userFilter) {
      params.responsableUserId = values.userFilter;
    }

    params.serviceId = values.serviceId || 0;
    params.statusId = values.statusFilter != undefined ? values.statusFilter : null;

    if (values?.intervalFilter?.length) {
      params.periodStart = moment(values.intervalFilter[0])
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSSZ');

      params.periodEnd = moment(values.intervalFilter[1])
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    }

    return params;
  };

  const cleanFilterFields = (): void => {
    searchFormInstance.setFieldsValue({
      serviceFilter: '',
      matricularionFilter: '',
      cooperativeFilter: '',
      propertyFilter: '',
      userFilter: undefined,
      statusFilter: undefined,
      serviceId: undefined,
      intervalFilter: [moment().subtract(30, 'day'), moment()],
    });
  };

  const fetchData = async (page: number): Promise<void> => {
    setLoading(true);

    const params = getFilterParameters();
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    let url = '/service/customer/listAllServiceRequest';
    if (userType === 'COOPERCITRUS') {
      url = '/service/coopercitrus/listAllServiceRequest';
    }

    const requestReference: RequestWrapper = patch({
      url,
      params,

      query: {
        page: page - 1,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 5008):
        // "All service requests listed successfully", 5008
        setData(response?.body.serviceRequests);
        setPaginationInfo((previous) => {
          return ({
            ...previous,
            ...response?.body.paginationInfo,
          });
        });
        setLoading(false);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5013):
        // "Invalid list service requests filters", -5013
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .InvalidListServiceRequestsFilters
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -19000):
        // "SAPTimeOut", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19001):
        // "SAPNotFound", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19002):
        // "SAPBadRequest", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -18000):
        // "Bp not found of SAP", -18000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.BP
            .BpNotFoundForSap.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar a listagem, por favor tente novamente.',
        });
        break;
    }

    setLoading(false);
  };

  const handleFilter = (): void => {
    fetchData(1);
  };

  const handleOkModal = async (): Promise<void> => {
    let values: {
      selectUser?: number,
    };

    setLoadingModal(true);

    try {
      values = await userFormInstance.validateFields(['selectUser']);
    } catch (error: unknown) {
      console.log(error);
      return;
    }

    const params = {
      serviceRequestId: currentModalServiceId,
      userId: values.selectUser,
    };

    setLoadingModal(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const targetSolicitation = data.find(
      (i) => {
        return i.id === currentModalServiceId;
      },
    );

    if (
      targetSolicitation?.serviceName === 'Pós-vendas - Assistência técnica'
      || targetSolicitation?.serviceName === 'Pós-vendas - Locação de piloto'
      || targetSolicitation?.serviceName === 'Pós-vendas - Montagem de equipamento'
    ) {
      setParamsResponsableUser(params);
      setVisibleModalResponsable(false);
      setLoadingModal(false);
      setModalScheduleDate(true);
    } else {
      const requestReference: RequestWrapper = put({
        url: '/service/setResponsableUser',
        params,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 5002):
        case (response?.body?.code === 5005): {
          // "Service requested successfully", 5002
          // "Responsable user set successfully", 5005
          let newData = data;

          const user = allActivatedUsers
            .find((v) => {
              return v.id.toString() === values.selectUser;
            });

          if (user) {
            newData = newData.map((v) => {
              if (v.id === currentModalServiceId) {
                return { ...v, responsableUserName: user.name };
              }

              return v;
            });

            setData(newData);
            setVisibleModalResponsable(false);
            userFormInstance.resetFields();
          } else {
            setVisibleModalResponsable(false);
            userFormInstance.resetFields();
            fetchData(1);
          }
        } break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.General
              .Forbidden.description.pt_br,
          });
          break;

        case (responseError?.body?.code === -5008):
          // "Service request not found", -5008
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.Service
              .ServiceRequestNotFound.description.pt_br,
          });
          break;

        case (responseError?.body?.code === -1007):
          // "User not found", -1007
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.User
              .UserNotFound.description.pt_br,
          });
          break;

        case (responseError?.body?.code === -5000):
          // "Service not found", -5000
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.Service
              .ServiceNotFound.description.pt_br,
          });
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Erro ao atribuir o usuário responsável.',
          });
          break;
      }

      setLoadingModal(false);
    }
  };

  const handleScheduleDatePart2 = async (
    params: {
      serviceRequestId: number;
      nextStep?: number;
      scheduleDate?: string;
    },
  ): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    // step 2: atualizar a data de agendamento

    const requestReference: RequestWrapper = put({
      url: '/service/updateServiceRequestStep',
      params,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 5007): {
        // "Service request successfully updated", 5007
        let newDataSchedule = data;

        newDataSchedule = newDataSchedule.map((v) => {
          if (v.id === currentModalServiceId) {
            return { ...v, schedulingDate: params.scheduleDate };
          }

          return v;
        });

        setData(newDataSchedule);
        setVisibleModalResponsable(false);
        setLoadingModal(false);
        setModalScheduleDate(false);
        clearFormModals();
        fetchData(1);
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5008):
        // "Service request not found", -5008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .ServiceRequestNotFound.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5012):
        // "Cannot update cancelled service request", -5012
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .CannotUpdateCancelledServiceRequest
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5009):
        // "Missing mandatory scheduled date", -5009
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .MissingScheduleDate.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5011):
        // "Past date entered", -5011
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .PastDateEntered.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5019):
        // "Next step not found", -5019
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .NextStepNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao atualizar o agendamento, '
            + 'por favor tente novamente mais tarde.',
        });
        break;
    }

    setLoadingModal(false);
  };

  const handleScheduleDate = async (): Promise<void> => {
    setLoadingModal(true);

    let values;
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const id = currentModalServiceId;

    const params: {
      serviceRequestId: number;
      nextStep?: number;
      scheduleDate?: string;
    } = {
      serviceRequestId: id,
    };

    params.nextStep = 3; // agendamento

    try {
      values = await scheduleFormInstance.validateFields();
    } catch (error: unknown) {
      setLoadingModal(false);
      return;
    }

    if (values.scheduleDate) {
      params.scheduleDate = values.scheduleDate;
    }

    // step 1: atualizar o usuário responsável

    const requestReference: RequestWrapper = put({
      url: '/service/setResponsableUser',
      params: paramsResponsableUser,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 5005):
        // "Responsable user set successfully", 5005
        await handleScheduleDatePart2(params);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5008):
        // "Service request not found", -5008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .ServiceRequestNotFound.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserNotFound.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5000):
        // "Service not found", -5000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .ServiceNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao atribuir o usuário responsável, '
            + 'por favor tente novamente mais tarde.',
        });
        break;
    }

    setLoadingModal(false);
  };

  const showModalResponsable = (currentModalServiceIdParameter: number): void => {
    setCurrentModalServiceId(currentModalServiceIdParameter);
    setVisibleModalResponsable(true);
  };

  const loadStatusData = async (id: number): Promise<void> => {
    setLoadingStatus(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: `/service/getServiceStatusFlow/${id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 5009): {
        // "Service flow listed successfully", 5009
        const newStatusesList: Array<FlowOutput> = response?.body?.serviceStatusFlowOutputs;

        setStatusesList(newStatusesList);

        const findLastIndex = (array: Array<FlowOutput>): number => {
          let finalIndex = 0;

          for (const ind in array) {
            if (array[ind]?.date != null) {
              finalIndex = ind;
            }
          }

          return finalIndex;
        };

        const statIndex = findLastIndex(newStatusesList);
        setCurrentStatusIndex(Number.parseInt(statIndex, 10));
        setCanceled(!!newStatusesList.find((v: FlowOutput) => v.reasonForCancellation));
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5008):
        // "Service request not found", -5008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .ServiceRequestNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar os detalhes, por favor tente novamente.',
        });
        break;
    }

    setLoadingStatus(false);
  };

  const showModalStatus = (id: number): void => {
    setVisibleModalStatus(true);
    loadStatusData(id);
  };

  const validateDateRange = (_rule: any, value: Array<Moment>): Promise<void | Error> => {
    if (
      moment(value[1])
        .subtract(maxPeriodOfDaysFilter - 1, 'day').startOf('day')
      >= moment(value[0]).endOf('day')
    ) {
      return Promise.reject(new Error(`A busca é limitada a um intervalo de ${maxPeriodOfDaysFilter} dias`));
    }

    return Promise.resolve();
  };

  useEffect(() => {
    const loadStatusList = async (): Promise<boolean> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/service/findAllStatuses',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 5004):
          // "All statuses listed successfully", 5004
          setStatusArray(response?.body.statuses);
          return true;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          return false;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.General
              .Forbidden.description.pt_br,
          });
          return false;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          return false;

        case (responseError?.body?.code === -4000):
        default:
          // "Something unexpected happened", -4000
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve erro ao carregar a listagem de serviços, '
              + 'por favor tente novamente mais tarde.',
          });
          return false;
      }
    };

    const runOnMount = async (): Promise<void> => {
      await loadStatusList();

      let response1: ResponseWrapper | undefined;
      let responseError1: ResponseWrapper | undefined;
      let genericError1: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/service/getMaxPeriodOfDaysFilter',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response1 = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError1 = error.response;
        } else {
          genericError1 = error as Error;
        }
      }

      switch (true) {
        case (response1?.body?.code === 5012):
          // "Max period of days filter got successfully", 5012
          setMaxPeriodOfDaysFilter(response1?.body?.maxPeriodOfDaysFilter);
          break;

        case (responseError1?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError1?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.General
              .Forbidden.description.pt_br,
          });
          break;

        case (genericError1?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        case (responseError1?.body?.code === -4000):
        default:
          // "Something unexpected happened", -4000
          // 'Erro ao carregar o período máximo de dias'
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao carregar o período máximo de dias, '
              + 'por favor tente novamente mais tarde.',
          });
          break;
      }

      if (userType === 'COOPERCITRUS') {
        let response2: ResponseWrapper | undefined;
        let responseError2: ResponseWrapper | undefined;
        let genericError2: Error | undefined;

        let response3: ResponseWrapper | undefined;
        let responseError3: ResponseWrapper | undefined;
        let genericError3: Error | undefined;

        const requestReference2: RequestWrapper = get({
          url: '/admin/findAllNotDeleted',

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        try {
          response2 = await requestReference2;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            responseError2 = error.response;
          } else {
            genericError2 = error as Error;
          }
        }

        switch (true) {
          case (response2?.body?.code === 1009):
            // "All users listed successfully", 1009
            setUserOptions(
              response2?.body?.coopercitrusUsers.map(
                (v: CoopercitrusUser) => (
                  <Select.Option
                    className="ListServices__UserSelectOption"
                    key={v.id}
                    value={v.id}
                  >
                    {v.name}
                  </Select.Option>
                ),
              ),
            );
            break;

          case (responseError2?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (responseError2?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.General
                .Forbidden.description.pt_br,
            });
            break;

          case (responseError2?.body?.code === -1007):
            // "User not found", -1007
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.User
                .UserNotFound.description.pt_br,
            });
            break;

          case (genericError2?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um erro ao carregar a listagem de usuários responsáveis, '
                + 'por favor tente novamente mais tarde.',
            });
            break;
        }

        const requestReference3: RequestWrapper = get({
          url: '/admin/findAllActivated',

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        try {
          response3 = await requestReference3;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            responseError3 = error.response;
          } else {
            genericError3 = error as Error;
          }
        }

        switch (true) {
          case (response3?.body?.code === 1009):
            setAllActivatedUsers(response3?.body?.coopercitrusUsers);

            setUserModalOptions(
              response3?.body?.coopercitrusUsers.map(
                (v: CoopercitrusUser) => (
                  <Select.Option
                    className="ListServices__UserModalSelectOption"
                    key={v.id}
                    value={v.id}
                  >
                    {v.name}
                  </Select.Option>
                ),
              ),
            );
            break;

          case (responseError3?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (responseError3?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.General
                .Forbidden.description.pt_br,
            });
            break;

          case (responseError3?.body?.code === -1007):
            // "User not found", -1007
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.User
                .UserNotFound.description.pt_br,
            });
            break;

          case (genericError3?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um erro ao carregar a listagem de usuários responsáveis, '
                + 'por favor tente novamente mais tarde.',
            });
            break;
        }
      } else {
        let response4: ResponseWrapper | undefined;
        let responseError4: ResponseWrapper | undefined;
        let genericError4: Error | undefined;

        const requestReference4: RequestWrapper = get({
          url: '/service/findAllServicesFilter',

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        try {
          response4 = await requestReference4;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            responseError4 = error.response;
          } else {
            genericError4 = error as Error;
          }
        }

        switch (true) {
          case (response4?.body?.code === 5011):
            // "All services filter listed successfully", 5011
            setServicesFilter(
              response4?.body?.services.map(
                (v: Service) => (
                  <Select.Option
                    className="ListServices__ServiceSelectOption"
                    key={v.id}
                    value={v.id}
                  >
                    {v.name}
                  </Select.Option>
                ),
              ),
            );
            break;

          case (responseError4?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (responseError4?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.General
                .Forbidden.description.pt_br,
            });
            break;

          case (genericError4?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          case (responseError4?.body?.code === -4000):
          default:
            // "Something unexpected happened", -4000
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um erro ao carregar a listagem de usuários responsáveis, '
                + 'por favor tente novamente mais tarde.',
            });
            break;
        }
      }

      fetchData(1);
    };

    runOnMount();

    return () => {
      setLoadingModal(false);
    };
  }, []);

  const exportExcel = async (): Promise<void> => {
    setLoadingExcel(true);

    const params = getFilterParameters();
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = post({
      url: '/service/export/excel',
      params,

      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },

      responseType: 'blob',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log(error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.status === 200): {
        const filee = new Blob([response?.body], { type: 'xlsx' });

        const linkElement = document.createElement('a');
        const downloadUrl = window.URL.createObjectURL(filee);

        const today = new Date();
        const date = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
        linkElement.href = downloadUrl;
        linkElement.download = `Servicos Solicitados ${date}.xlsx`;

        document.body.append(linkElement);

        linkElement.click();
        setLoadingExcel(false);

        setTimeout(() => {
          linkElement.remove();
          window.URL.revokeObjectURL(downloadUrl);
        }, 0);
      } break;

      case (responseError?.status === 401):
        break;

      case (responseError?.status === 403):
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.status === 400):
      case (responseError?.status === 408):
      case (responseError?.status === 500):
      default:
        // Bad request 400
        // Request timeout 408

        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao baixar o arquivo, por favor tente novamente mais tarde',
        });
        break;
    }

    setLoadingExcel(false);
  };

  return (
    <div
      className="ListServices"
    >
      <Spin
        className="ListServices__Spin"
        tip="Carregando..."
        spinning={loading}
      >
        <Row
          gutter={24}
          className="title-container"
        >
          <Col
            xs={{ span: 24 }}
            md={{ span: 12 }}
            className="ListServices__PageTitleColumn"
          >
            {
              userType === 'COOPERCITRUS'
                ? (
                  <span className="page-title">Controle de serviços solicitados</span>
                )
                : (
                  <span className="page-title">Serviços solicitados</span>
                )
            }
          </Col>

          <Col
            xs={{ span: 24 }}
            md={{ span: 12 }}
          >
            <div
              className="btns-action-position"
            >
              <div className="btns-action-flex">
                <Button
                  className="add-new-btn"
                  type="primary"
                  onClick={exportExcel}
                  loading={loadingExcel}
                >
                  Exportar
                </Button>
              </div>

              <div className="btns-action-flex">
                <Button
                  className="add-new-btn"
                  type="primary"
                  onClick={
                    (): void => {
                      navigate('/service/new');
                    }
                  }
                >
                  Criar nova solicitação de serviço
                </Button>
              </div>

            </div>
          </Col>
        </Row>

        <Row
          gutter={24}
          className="filters-container"
        >
          <Col
            xs={{ span: 24 }}
            className="ListServices__FormColumn"
          >
            <Form
              scrollToFirstError
              layout="vertical"
              className="ListServices__Form"
              form={searchFormInstance}
              onFinish={handleFilter}
              onFinishFailed={
                (errorInfo): void => {
                  console.log('onFinishFailed filterform', errorInfo);
                }
              }
            >
              <Row
                gutter={24}
                className="ListServices__FormRow ListServices__FirstFormRow"
              >
                {
                  userType === 'COOPERCITRUS'
                  && (
                    <>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServices__ResponsibleUserFilterColumn"
                      >
                        <Form.Item
                          label="Usuário responsável"
                          className="ListServices__ResponsibleUserFilterFormItem"
                          name="userFilter"
                          rules={[]}
                        >
                          <Select
                            showSearch
                            className="ListServices__ResponsibleUserFilterSelect"
                            allowClear
                            style={{ width: '100%' }}
                            placeholder="Selecione o usuário responsável"
                            filterOption={filterOptions}
                          >
                            {userOptions}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServices__RegistrationFilterColumn"
                      >
                        <Form.Item
                          label="BP"
                          className="ListServices__RegistrationFilterFormItem"
                          name="matricularionFilter"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            maxLength={10}
                            type="number"
                            min={0}
                            onBlur={
                              (eventt): void => {
                                searchFormInstance.setFieldsValue({
                                  matricularionFilter: eventt.target.value.replace(/.*/, formatBP(eventt.target.value)),
                                });
                              }
                            }
                            placeholder="Digite o BP"
                            className="ListServices__RegistrationFilterInput"
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServices__CooperativeFilterColumn"
                      >
                        <Form.Item
                          label="Cooperado"
                          className="ListServices__CooperativeFilterFormItem"
                          name="cooperativeFilter"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            placeholder="Digite o cooperado"
                            className="ListServices__CooperativeFilterInput"
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServices__PropertyFilterColumn"
                      >
                        <Form.Item
                          label="Propriedade"
                          name="propertyFilter"
                          className="ListServices__PropertyFilterFormItem"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            placeholder="Digite a propriedade"
                            className="ListServices__PropertyFilterInput"
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )
                }

                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 6 }}
                  className="ListServices__ServiceFilterColumn"
                >
                  {
                    userType === 'COOPERCITRUS'
                      ? (
                        <Form.Item
                          label="Serviço"
                          name="serviceFilter"
                          className="ListServices__ServiceFilterFormItem"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            placeholder="Digite o serviço"
                            className="ListServices__ServiceFilterInput"
                          />
                        </Form.Item>
                      )
                      : (
                        <Form.Item
                          label="Serviço"
                          name="serviceId"
                          className="ListServices__ServiceIdFilterFormItem"
                          rules={[]}
                        >
                          <Select
                            className="ListServices__ServiceIdFilterSelect"
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="Selecione o serviço"
                          >
                            {servicesFilter}
                          </Select>
                        </Form.Item>
                      )
                  }
                </Col>

                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 6 }}
                  className="ListServices__StatusFilterColumn"
                >
                  <Form.Item
                    label="Status"
                    name="statusFilter"
                    className="ListServices__StatusFilterFormItem"
                    rules={[]}
                  >
                    <Select
                      showSearch
                      className="ListServices__StatusFilterSelect"
                      style={{ width: '100%' }}
                      placeholder="Selecione o status"
                    >
                      {
                        statusArray.map((v) => (
                          <Select.Option
                            key={v.id}
                            value={v.id}
                            className="ListServices__StatusFilterSelectOption"
                          >
                            {v.name}
                          </Select.Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>

                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 6 }}
                  className="ListServices__IntervalFilterColumn"
                >
                  <Form.Item
                    label="Período"
                    name="intervalFilter"
                    className="ListServices__IntervalFilterFormItem"
                    rules={[{ validator: validateDateRange }]}
                    initialValue={[moment().subtract(30, 'day'), moment()]}
                  >
                    <DatePicker.RangePicker
                      className="ListServices__IntervalFilterDatepicker"
                      style={{ width: '100%' }}
                      placeholder={['Início', 'Fim']}
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>

                {
                  userType === 'COOPERCITRUS'
                    ? (
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServices__CTCFilterColumn"
                      >
                        <Form.Item
                          className="ListServices__CTCFilterFormItem"
                          label="Vendedor Responsável"
                          name="ctcFilter"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            placeholder="Digite o nome"
                            className="ListServices__CTCFilterInput"
                          />
                        </Form.Item>
                      </Col>
                    )
                    : null
                }
              </Row>

              <Row
                gutter={24}
                justify="end"
                className="ListServices__FilterButtonsRow"
              >
                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 6 }}
                  className="ListServices__FilterButtonsColumn"
                >
                  <Row
                    className="ListServices__ButtonsParentRow"
                  >
                    <Col
                      xs={{ span: 12 }}
                      lg={{ span: 12 }}
                      className="ListServices__CleanFilterButtonColumn"
                    >
                      <Form.Item
                        className="ListServices__CleanFilterButtonFormItem"
                      >
                        <Button
                          className="btn-clean"
                          onClick={
                            (): void => {
                              cleanFilterFields();
                            }
                          }
                          htmlType="reset"
                        >
                          <FilterRemoveIcon
                            className="ListServices__CleanFilterButtonIcon"
                          />
                          <span>Limpar</span>
                        </Button>
                      </Form.Item>
                    </Col>
                    <Col
                      xs={{ span: 12 }}
                      lg={{ span: 12 }}
                      className="ListServices__SubmitFilterButtonColumn"
                    >
                      <Form.Item
                        className="ListServices__SubmitFilterFormItem"
                      >
                        <Button
                          className="btn-filter"
                          type="primary"
                          htmlType="submit"
                        >
                          <FilterIcon
                            className="ListServices__SubmitFilterButtonIcon"
                          />
                          <span>Filtrar</span>
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>

        <Row
          gutter={24}
          className="list-container"
        >
          <Col
            span={24}
            className="ListServices__TAbleParentColumn"
          >
            <Table
              className="list-table"
              rowKey="id"
              columns={
                [
                  ...(
                    userType === 'COOPERCITRUS'
                      ? [
                        {
                          title: <div className="table-titles">ID</div>,
                          dataIndex: 'id',
                          className: 'ListServices__TableColumn',
                          key: 'id',
                        },
                      ]
                      : []
                  ),
                  {
                    title: <div className="table-titles">Data/hora da solicitação</div>,
                    dataIndex: 'solicitationDateTime',
                    className: 'ListServices__TableColumn',
                    key: 'solicitationDateTime',

                    render: (date: string) => (date ? moment(date).format('DD/MM/YYYY [às] HH:mm') : ''),
                  },
                  {
                    title: <div className="table-titles">Serviço</div>,
                    dataIndex: 'serviceName',
                    className: 'ListServices__TableColumn',
                    key: 'serviceName',
                  },
                  {
                    title: <div className="table-titles">Propriedade</div>,
                    dataIndex: 'landName',
                    className: 'ListServices__TableColumn',
                    key: 'landName',
                  },
                  {
                    title: <div className="table-titles">Cooperado</div>,
                    key: 'landOwnerName',
                    dataIndex: 'landOwnerName',
                    className: 'ListServices__TableColumn',
                  },
                  ...(userType === 'COOPERCITRUS' ? [{
                    title: <div className="table-titles">Usuário Responsável</div>,
                    key: 'responsableUserName',
                    dataIndex: 'responsableUserName',
                    className: 'ListServices__TableColumn',

                    render: (
                      responsableUserName: string,
                      record: ServiceRequestForCustomer | ServiceRequestForCoopercitrus,
                    ) => (
                      <div
                        className="user-container"
                      >
                        {responsableUserName}
                        <SquareEditOutlineIcon
                          className="ListServices__EditResponsableIcon"
                          onClick={
                            (): void => {
                              showModalResponsable(record.id);
                            }
                          }
                        />
                      </div>
                    ),
                  }] : []),
                  {
                    title: <div className="table-titles">Status</div>,
                    key: 'status',
                    dataIndex: 'status',
                    className: 'ListServices__TableColumn',

                    render: (
                      textt: string,
                      record: ServiceRequestForCustomer | ServiceRequestForCoopercitrus,
                    ): JSX.Element => {
                      if (textt === 'Cancelado') {
                        return (
                          <div
                            className="status-container-btn"
                          >
                            <span>Cancelado</span>
                            <InformationOutlineIcon
                              className="ListServices__CancelledStatusIcon"
                              onClick={
                                (): void => {
                                  showModalStatus(record.id);
                                }
                              }
                            />
                          </div>
                        );
                      }

                      if (textt === 'Concluído') {
                        return (
                          <div
                            className="status-container-btn"
                          >
                            <span>Concluído</span>
                            <InformationOutlineIcon
                              className="ListServices__ConcludedStatusIcon"
                              onClick={
                                (): void => {
                                  showModalStatus(record.id);
                                }
                              }
                            />
                          </div>
                        );
                      }

                      return (
                        <div
                          className="status-container-btn"
                        >
                          <span>{textt}</span>
                          {
                            userType === 'COOPERCITRUS'
                              ? (
                                <a
                                  href={`/service/status/${record.id}`}
                                  className="ListServices__StatusLink"
                                  onClick={
                                    (eventt): void => {
                                      eventt.preventDefault();
                                      navigate(`/service/status/${record.id}`);
                                    }
                                  }
                                >
                                  <SquareEditOutlineIcon
                                    className="ListServices__EditStatusIcon"
                                  />
                                </a>
                              )
                              : (
                                <SquareEditOutlineIcon
                                  className="ListServices__ShowStatusIcon"
                                  onClick={
                                    (): void => {
                                      showModalStatus(record.id);
                                    }
                                  }
                                />
                              )
                          }
                        </div>
                      );
                    },
                  },
                  {
                    title: <div className="table-titles">Data agendada</div>,
                    key: 'schedulingDate',
                    dataIndex: 'schedulingDate',
                    className: 'ListServices_TableColumn',
                    render: (date: string) => (date ? moment(date, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]').format('DD/MM/YYYY') : '-'),
                  },
                  {
                    title: '',
                    key: 'details',
                    className: 'ListServices__TableColumn',

                    render: (
                      _text: string,
                      record: ServiceRequestForCustomer | ServiceRequestForCoopercitrus,
                    ) => (
                      <a
                        className="actions-btn"
                        href={`/service/detail/${record.id}`}
                        onClick={
                          (eventt): void => {
                            eventt.preventDefault();
                            navigate(`/service/detail/${record.id}`);
                          }
                        }
                      >
                        Detalhes
                      </a>
                    ),
                  },
                ]
              }
              dataSource={data}
              pagination={false}
            />
          </Col>
          <Col
            span={24}
            className="ListServices__CustomPaginationColumn"
          >
            <CustomPagination
              paginationInfo={paginationInfo}
              fetchFunc={fetchData}
            />
          </Col>
        </Row>

        <Modal
          afterClose={clearFormModals}
          className="ListServices__UserModal"
          wrapClassName="ListServices-user-modal"
          title={<h3 className="ListServices__UserModalTitle" style={{ margin: 0 }}>Atribuir usuário</h3>}
          visible={visibleModalResponsable}
          onOk={handleOkModal}
          onCancel={
            (): void => {
              setVisibleModalResponsable(false);
            }
          }
          confirmLoading={loadingModal}
          footer={[
            <Button
              className="btn-ok"
              type="primary"
              key="submit"
              loading={loadingModal}
              onClick={handleOkModal}
            >
              Salvar
            </Button>,
          ]}
        >
          {
            visibleModalResponsable
            && (
              <Form
                scrollToFirstError
                className="ListServices__UserForm"
                form={userFormInstance}
              >
                <Form.Item
                  className="ListServices__SetResponsibleUserFormItem"
                  label=""
                  name="selectUser"
                  rules={[{ required: true, message: 'Por favor, insira o usuário responsável.' }]}
                  initialValue={undefined}
                >
                  <Select
                    className="ListServices__SetResponsibleUserSelect"
                    showSearch
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Digite o usuário responsável"
                    filterOption={filterOptions}
                  >
                    {userModalOptions}
                  </Select>
                </Form.Item>
              </Form>
            )
          }
        </Modal>

        <Modal
          className="ListServices__StatusModal"
          wrapClassName="ListServices-status-modal"
          width={1000}
          title={<h3 className="ListServices__StatusModalTitle" style={{ margin: 0 }}>Status da solicitação</h3>}
          visible={visibleModalStatus}
          onCancel={
            (): void => {
              setVisibleModalStatus(false);
            }
          }
          footer={[]}
        >
          {
            visibleModalStatus
            && !loadingStatus
            && (
              <ServiceStatusComponent
                loading={loadingStatus}
                statusesList={statusesList}
                currentStatusIndex={currentStatusIndex}
                canceled={canceled}
              />
            )
          }
        </Modal>

        <Modal
          wrapClassName="ListServices-status-modal"
          title={<h3 style={{ margin: 0 }}>Data de agendamento</h3>}
          visible={modalScheduleDate}
          onOk={handleScheduleDate}
          onCancel={
            (): void => {
              setModalScheduleDate(false);
            }
          }
          confirmLoading={loadingModal}
          footer={[
            <Button
              key="back"
              onClick={
                (): void => {
                  userFormInstance.resetFields();
                  scheduleFormInstance.resetFields();
                  setModalScheduleDate(false);
                }
              }
            >
              Cancelar
            </Button>,
            <Button
              type="primary"
              key="submit"
              loading={loadingModal}
              onClick={handleScheduleDate}
            >
              Confirmar
            </Button>,
          ]}
          afterClose={clearFormModals}
        >
          <Form
            scrollToFirstError
            form={scheduleFormInstance}
          >
            <Form.Item
              label="Data de agendamento"
              name="scheduleDate"
              rules={[
                { validator: validateScheduleDate },
                { required: true, message: ' ' },
              ]}
              initialValue={moment()}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Data de agendamento"
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </div>
  );
};

ListServices.propTypes = {};

ListServices.defaultProps = {};

export default ListServices;
